// apps/client/src/components/leads/crmClassificationMap/crmClassificationMap.ts
//
// Pure helpers for the classification -> CRM value map editor.
//
// The stored map (companies/{c}/crm_classification_maps/{provider}) is
//   { [dimension]: { [classification value id]: provider value } }
// validated server side by ClassificationCrmMapSchema
// (packages/shared-crm/src/crmWriteBack.ts). shared-crm is a Node package
// (it bundles the MyCRM HTTP provider), so the browser mirrors its shape and
// limits here instead of importing it; a test pins the two together.

import {
  CLASSIFICATION_DIMENSIONS,
  DEFAULT_CLASSIFICATION_OPTIONS,
  type ClassificationDimension,
  type ClassificationOption,
} from '@valutten/shared-schemas/dist/leadClassification';

export type CrmClassificationMap = Partial<Record<ClassificationDimension, Record<string, string>>>;

/** Mirrors ClassificationCrmMapSchema: value ids and the provider value limit. */
export const CRM_MAP_VALUE_ID_PATTERN = /^[a-z][a-z0-9_]{0,40}$/;
export const CRM_MAP_PROVIDER_VALUE_MAX = 120;

/** Providers the lead write-back registry knows (shared-crm getLeadWriteBackAdapter). */
export const CRM_MAP_PROVIDER_LABELS: Readonly<Record<string, string>> = {
  mycrm: 'MyCRM',
  connective: 'Connective',
  afg: 'AFG',
  salestrekker: 'Salestrekker',
  monday: 'Monday',
  smokeball: 'Smokeball',
};

/** What MyCRM uses when a loan structure has no tenant mapping (DEFAULT_MYCRM_PRODUCT_LINE_BY_STRUCTURE). */
export const MYCRM_DEFAULT_PRODUCT_LINES: Readonly<Record<string, string>> = {
  standard_residential: 'Residential',
  commercial: 'Commercial',
};

export interface MapRowOption {
  id: string;
  label: string;
}

/**
 * Every classification value the tenant can see, per dimension: the default
 * mortgage options plus each service category's configured options from the
 * taxonomy doc, de-duplicated by id (the first label seen wins), inactive
 * options kept (a lead classified before an option was retired still maps).
 */
export function collectClassificationValues(
  taxonomyClassification: Record<string, Partial<Record<ClassificationDimension, ClassificationOption[]>>> | null | undefined,
): Record<ClassificationDimension, MapRowOption[]> {
  const out = {} as Record<ClassificationDimension, MapRowOption[]>;
  for (const dim of CLASSIFICATION_DIMENSIONS) {
    const seen = new Map<string, string>();
    const sources: Array<readonly ClassificationOption[] | undefined> = [
      DEFAULT_CLASSIFICATION_OPTIONS[dim],
      ...Object.keys(taxonomyClassification ?? {})
        .sort()
        .map((cat) => taxonomyClassification?.[cat]?.[dim]),
    ];
    for (const list of sources) {
      for (const o of list ?? []) {
        if (!o || typeof o.id !== 'string' || !CRM_MAP_VALUE_ID_PATTERN.test(o.id) || seen.has(o.id)) continue;
        seen.set(o.id, typeof o.label === 'string' && o.label.trim() ? o.label.trim() : o.id);
      }
    }
    out[dim] = Array.from(seen, ([id, label]) => ({ id, label }));
  }
  return out;
}

/** Read a stored map defensively: unknown dimensions, bad ids and non-strings are dropped. */
export function sanitiseCrmMap(raw: unknown): CrmClassificationMap {
  const out: CrmClassificationMap = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const dim of CLASSIFICATION_DIMENSIONS) {
    const d = (raw as Record<string, unknown>)[dim];
    if (!d || typeof d !== 'object') continue;
    const clean: Record<string, string> = {};
    for (const [id, v] of Object.entries(d as Record<string, unknown>)) {
      if (CRM_MAP_VALUE_ID_PATTERN.test(id) && typeof v === 'string' && v.trim()) clean[id] = v.trim();
    }
    if (Object.keys(clean).length > 0) out[dim] = clean;
  }
  return out;
}

/**
 * The map to save from the editor's text inputs: blanks dropped (unmapped),
 * values trimmed, empty dimensions omitted. Returns errors instead when a
 * value is too long.
 */
export function buildCrmMapForSave(
  edits: Partial<Record<ClassificationDimension, Record<string, string>>>,
): { ok: true; map: CrmClassificationMap } | { ok: false; errors: string[] } {
  const map: CrmClassificationMap = {};
  const errors: string[] = [];
  for (const dim of CLASSIFICATION_DIMENSIONS) {
    const d = edits[dim];
    if (!d) continue;
    const clean: Record<string, string> = {};
    for (const [id, raw] of Object.entries(d)) {
      const v = (raw ?? '').trim();
      if (!v) continue;
      if (!CRM_MAP_VALUE_ID_PATTERN.test(id)) continue;
      if (v.length > CRM_MAP_PROVIDER_VALUE_MAX) {
        errors.push(`"${v.slice(0, 30)}…" is longer than ${CRM_MAP_PROVIDER_VALUE_MAX} characters.`);
        continue;
      }
      clean[id] = v;
    }
    if (Object.keys(clean).length > 0) map[dim] = clean;
  }
  return errors.length > 0 ? { ok: false, errors } : { ok: true, map };
}

/**
 * The providers getCrmClassificationMap reports for this tenant (`providers`
 * in its response), as ids: strings, or objects carrying `id` / `provider`,
 * trimmed and de-duplicated in server order. Null when the response carried
 * no usable list, so the editor keeps the providers it was given.
 */
export function normaliseCrmMapProviders(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out: string[] = [];
  for (const p of raw as unknown[]) {
    const obj = p && typeof p === 'object' ? (p as { id?: unknown; provider?: unknown }) : null;
    const v = typeof p === 'string' ? p : typeof obj?.id === 'string' ? obj.id : obj?.provider;
    const id = typeof v === 'string' ? v.trim() : '';
    if (id && !out.includes(id)) out.push(id);
  }
  return out.length > 0 ? out : null;
}
